import React, { useState, useEffect } from 'react';
import { X, Copy } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clinicApi } from '../services/clinicApi';
import type { UpdateOperatingHoursRequest } from '../services/clinicApi';
import type { ClinicDetailDto } from '../types/clinic';
import './EditOperatingHoursModal.css';

interface EditOperatingHoursModalProps {
  clinic: ClinicDetailDto;
  onClose: () => void;
  onSave: () => void;
}

interface DayHours {
  isClosed: boolean;
  openTime: string;
  closeTime: string;
}

interface FormState {
  monday: DayHours;
  tuesday: DayHours;
  wednesday: DayHours;
  thursday: DayHours;
  friday: DayHours;
  saturday: DayHours;
  sunday: DayHours;
}

const DEFAULT_OPEN = '08:00';
const DEFAULT_CLOSE = '17:00';

// Generate time options for select dropdowns (30-minute intervals)
const generateTimeOptions = (): { value: string; label: string }[] => {
  const options: { value: string; label: string }[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const h = hour.toString().padStart(2, '0');
      const m = minute.toString().padStart(2, '0');
      const value = `${h}:${m}`;

      // Format for display (12-hour with AM/PM)
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour < 12 ? 'AM' : 'PM';
      const label = `${displayHour}:${m.padStart(2, '0')} ${ampm}`;

      options.push({ value, label });
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_KEYS: (keyof FormState)[] = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
];

const EditOperatingHoursModal: React.FC<Readonly<EditOperatingHoursModalProps>> = ({
  clinic,
  onClose,
  onSave,
}) => {
  const queryClient = useQueryClient();

  // Helper to parse time from clinic data (handles "HH:mm" or "HH:mm:ss" format)
  const parseTime = (time: string | null | undefined): string => {
    if (!time) return '';
    // Take only HH:mm portion
    const parts = time.split(':');
    if (parts.length >= 2) {
      return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    }
    return time;
  };

  // Initialize form state from clinic data
  const initializeDayHours = (start: string | null, end: string | null): DayHours => {
    const hasHours = start && end;
    return {
      isClosed: !hasHours,
      openTime: hasHours ? parseTime(start) : DEFAULT_OPEN,
      closeTime: hasHours ? parseTime(end) : DEFAULT_CLOSE,
    };
  };

  const [formState, setFormState] = useState<FormState>({
    monday: initializeDayHours(clinic.monStart, clinic.monEnd),
    tuesday: initializeDayHours(clinic.tueStart, clinic.tueEnd),
    wednesday: initializeDayHours(clinic.wedStart, clinic.wedEnd),
    thursday: initializeDayHours(clinic.thurStart, clinic.thurEnd),
    friday: initializeDayHours(clinic.friStart, clinic.friEnd),
    saturday: initializeDayHours(clinic.satStart, clinic.satEnd),
    sunday: initializeDayHours(clinic.sunStart, clinic.sunEnd),
  });

  const [error, setError] = useState<string | null>(null);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const mutation = useMutation({
    mutationFn: (request: UpdateOperatingHoursRequest) =>
      clinicApi.updateOperatingHours(clinic.id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic', clinic.id] });
      onSave();
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to update operating hours');
    },
  });

  const handleDayChange = (
    day: keyof FormState,
    field: keyof DayHours,
    value: string | boolean
  ) => {
    setFormState((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value,
      },
    }));
  };

  const handleCopyMondayToWeekdays = () => {
    const mondayHours = formState.monday;
    setFormState((prev) => ({
      ...prev,
      tuesday: { ...mondayHours },
      wednesday: { ...mondayHours },
      thursday: { ...mondayHours },
      friday: { ...mondayHours },
    }));
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate: open time should be before close time for non-closed days
    for (const dayKey of DAY_KEYS) {
      const day = formState[dayKey];
      if (!day.isClosed && day.openTime >= day.closeTime) {
        setError(`${dayKey.charAt(0).toUpperCase() + dayKey.slice(1)}: Open time must be before close time`);
        return;
      }
    }

    // Build request - send null for closed days
    const request: UpdateOperatingHoursRequest = {
      monStart: formState.monday.isClosed ? null : formState.monday.openTime,
      monEnd: formState.monday.isClosed ? null : formState.monday.closeTime,
      tueStart: formState.tuesday.isClosed ? null : formState.tuesday.openTime,
      tueEnd: formState.tuesday.isClosed ? null : formState.tuesday.closeTime,
      wedStart: formState.wednesday.isClosed ? null : formState.wednesday.openTime,
      wedEnd: formState.wednesday.isClosed ? null : formState.wednesday.closeTime,
      thurStart: formState.thursday.isClosed ? null : formState.thursday.openTime,
      thurEnd: formState.thursday.isClosed ? null : formState.thursday.closeTime,
      friStart: formState.friday.isClosed ? null : formState.friday.openTime,
      friEnd: formState.friday.isClosed ? null : formState.friday.closeTime,
      satStart: formState.saturday.isClosed ? null : formState.saturday.openTime,
      satEnd: formState.saturday.isClosed ? null : formState.saturday.closeTime,
      sunStart: formState.sunday.isClosed ? null : formState.sunday.openTime,
      sunEnd: formState.sunday.isClosed ? null : formState.sunday.closeTime,
    };

    mutation.mutate(request);
  };

  return (
    <div className="edit-hours-modal-overlay" onClick={handleOverlayClick}>
      <div className="edit-hours-modal">
        <div className="edit-hours-modal-header">
          <h3>Edit Operating Hours</h3>
          <button
            className="edit-hours-modal-close"
            onClick={onClose}
            type="button"
            disabled={mutation.isPending}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="edit-hours-modal-body">
            {error && <div className="edit-hours-error">{error}</div>}

            <div className="hours-toolbar">
              <button
                type="button"
                className="btn-copy-hours"
                onClick={handleCopyMondayToWeekdays}
                title="Copy Monday's hours to Tuesday through Friday"
              >
                <Copy size={16} />
                Copy Monday to Weekdays
              </button>
            </div>

            <div className="hours-grid">
              <div className="hours-grid-header">
                <div className="col-day">Day</div>
                <div className="col-closed">Closed</div>
                <div className="col-open">Open</div>
                <div className="col-close">Close</div>
              </div>

              {DAY_KEYS.map((dayKey, index) => {
                const day = formState[dayKey];
                const isWeekend = index === 5 || index === 6;

                return (
                  <div
                    key={dayKey}
                    className={`hours-grid-row ${isWeekend ? 'weekend' : ''}`}
                  >
                    <div className="col-day">
                      <span className="day-name">{DAY_LABELS[index]}</span>
                      <span className="day-abbrev">{DAY_LABELS[index].substring(0, 3)}</span>
                    </div>

                    <div className="col-closed">
                      <label className="closed-checkbox">
                        <input
                          type="checkbox"
                          checked={day.isClosed}
                          onChange={(e) =>
                            handleDayChange(dayKey, 'isClosed', e.target.checked)
                          }
                        />
                        <span className="checkbox-label-text">Closed</span>
                      </label>
                    </div>

                    <div className="col-open">
                      <select
                        value={day.openTime}
                        onChange={(e) =>
                          handleDayChange(dayKey, 'openTime', e.target.value)
                        }
                        disabled={day.isClosed}
                        className={day.isClosed ? 'disabled' : ''}
                        aria-label={`${DAY_LABELS[index]} open time`}
                      >
                        {TIME_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-close">
                      <select
                        value={day.closeTime}
                        onChange={(e) =>
                          handleDayChange(dayKey, 'closeTime', e.target.value)
                        }
                        disabled={day.isClosed}
                        className={day.isClosed ? 'disabled' : ''}
                        aria-label={`${DAY_LABELS[index]} close time`}
                      >
                        {TIME_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                );
              })}
            </div>

          </div>

          <div className="edit-hours-modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={mutation.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditOperatingHoursModal;
